import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import API_CONFIG from '../utils/apiConfig';
import './EmailVerification.css';

const EmailVerification = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [verified, setVerified] = useState(false);

  const token = searchParams.get('token');

  useEffect(() => {
    if (token) {
      verifyEmail();
    }
  }, [token]);

  const verifyEmail = async () => {
    if (!token) {
      setError('No verification token provided');
      return;
    }

    setLoading(true);
    setError(''); // Clear error immediately
    setSuccess(''); // Clear success immediately
    setVerified(false); // Reset verified state

    try {
      const response = await fetch(API_CONFIG.getApiUrl(API_CONFIG.endpoints.verifyEmail), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token }),
      });

      const data = await response.json();

      if (response.ok) {
        setError(''); // Clear any error messages first
        setSuccess(data.message || 'Email verified successfully! You can now log in to your account.');
        setVerified(true);
      } else {
        // Handle different error response formats
        let errorMessage = 'Email verification failed';
        
        if (typeof data.detail === 'string') {
          errorMessage = data.detail;
        } else if (Array.isArray(data.detail)) {
          errorMessage = data.detail.map(err => err.msg || err.message || JSON.stringify(err)).join(', ');
        } else if (data.detail && typeof data.detail === 'object') {
          errorMessage = data.detail.message || data.detail.msg || JSON.stringify(data.detail);
        } else if (data.message) {
          errorMessage = data.message;
        }
        
        setError(errorMessage);
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await fetch(API_CONFIG.getApiUrl(API_CONFIG.endpoints.resendVerification), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: '' }), // We'll need to get this from somewhere
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess('Verification email sent successfully!');
      } else {
        // Handle different error response formats
        let errorMessage = 'Failed to send verification email';
        
        if (typeof data.detail === 'string') {
          errorMessage = data.detail;
        } else if (Array.isArray(data.detail)) {
          errorMessage = data.detail.map(err => err.msg || err.message || JSON.stringify(err)).join(', ');
        } else if (data.detail && typeof data.detail === 'object') {
          errorMessage = data.detail.message || data.detail.msg || JSON.stringify(data.detail);
        } else if (data.message) {
          errorMessage = data.message;
        }
        
        setError(errorMessage);
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="email-verification-container">
      <div className="email-verification-form">
        <div className="verification-header">
          <div className="verification-icon">
            {verified ? (
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                <polyline points="22,4 12,14.01 9,11.01"/>
              </svg>
            ) : (
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                <polyline points="22,6 12,13 2,6"/>
              </svg>
            )}
          </div>
          
          <h2>{verified ? 'Email Verified!' : 'Verify Your Email'}</h2>
          <p>
            {verified 
              ? 'Your email has been successfully verified. You can now log in to your account.'
              : 'Please check your email and click the verification link to activate your account.'
            }
          </p>
        </div>

        {loading && (
          <div className="loading-message">
            <div className="spinner"></div>
            <p>Verifying your email...</p>
          </div>
        )}

        {error && !success && !verified && (
          <div className="error-message">
            <p>{error}</p>
            {!verified && (
              <button 
                type="button" 
                className="resend-btn"
                onClick={handleResendVerification}
                disabled={loading}
              >
                Resend Verification Email
              </button>
            )}
          </div>
        )}

        {success && (
          <div className="success-message">
            <p>{success}</p>
          </div>
        )}

        {!token && !verified && (
          <div className="no-token-message">
            <p>No verification token found. Please check your email for the verification link.</p>
            <button 
              type="button" 
              className="resend-btn"
              onClick={handleResendVerification}
              disabled={loading}
            >
              Resend Verification Email
            </button>
          </div>
        )}

        <div className="verification-footer">
          {/* <p>
            Didn't receive the email? Check your spam folder or{' '}
            <button 
              type="button" 
              className="link-btn"
              onClick={handleResendVerification}
              disabled={loading}
            >
              resend verification email
            </button>
          </p> */}
          
          <div className="back-to-login">
            <a href="/login">Back to Login</a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailVerification;